// remotion/src/Whiteboard.tsx
import {
  fontFamily as permanentMarkerFamily,
  loadFont as loadPermanentMarker,
} from "@remotion/google-fonts/PermanentMarker";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useThree } from "@react-three/fiber";
import {
  continueRender,
  delayRender,
  Easing,
  interpolate,
  useCurrentFrame,
} from "remotion";
import { CanvasTexture } from "three";
import {
  PHONE_CURVE_SEGMENTS,
  WHITEBOARD_TILT,
  Z_FLICKER_PREVENTION,
  getWhiteboardSettledPosition,
  getWhiteboardSlideInStartPosition,
  type WhiteboardLayout,
} from "./helpers/layout";
import { roundedRect } from "./helpers/rounded-rectangle";
import { RoundedBox } from "./RoundedBox";
import {
  WHITEBOARD_SLIDE_IN_END_FRAME,
  WHITEBOARD_SLIDE_IN_START_FRAME,
} from "./helpers/timeline";

// Permanent Marker — bold, slightly uneven marker-pen handwriting.
// loadFont is called at module scope so the renderer waits for the font
// before painting any frame; without this the canvas falls back to
// the system sans-serif, defeating the "written by hand" look.
loadPermanentMarker("normal", {
  weights: ["400"],
  subsets: ["latin"],
});

const WHITEBOARD_FRAME_COLOR = "#1f2937";
const WHITEBOARD_SCREEN_COLOR = "#f5f5f0";
const WORKOUT_TITLE_COLOR = "#1c1c1c";
const WORKOUT_BODY_COLOR = "#2b2b2b";
const WORKOUT_ACCENT_COLOR = "#d6336c";
const HANDWRITTEN_FONT_STACK =
  `"${permanentMarkerFamily}", "Marker Felt", "Comic Sans MS", cursive`;

const TEXT_CANVAS_W = 1024;
const TEXT_CANVAS_H = 720;

const lerpVec3 = (
  a: [number, number, number],
  b: [number, number, number],
  t: number,
): [number, number, number] => [
  a[0] + (b[0] - a[0]) * t,
  a[1] + (b[1] - a[1]) * t,
  a[2] + (b[2] - a[2]) * t,
];

// Tiny per-line jitter so each line of the workout sits at a slightly
// different vertical and horizontal offset — the kind of imperfection
// you get when writing on a real whiteboard. Deterministic from the
// line index so the same lines always jitter the same amount across
// renders.
const handJitter = (
  seed: number,
): { dx: number; dy: number; rotate: number } => {
  // Cheap deterministic PRNG: a sin-hash that's stable across runs and
  // returns values in roughly [-1, 1].
  const noise = (n: number) => Math.sin(n * 12.9898 + 78.233) * 43758.5453;
  const r = (s: number) => {
    const v = noise(s);
    return v - Math.floor(v);
  };
  return {
    dx: (r(seed) - 0.5) * 6,
    dy: (r(seed + 1) - 0.5) * 5,
    rotate: (r(seed + 2) - 0.5) * 0.04, // ~±2.3°
  };
};

const drawWorkout = (
  ctx: OffscreenCanvasRenderingContext2D,
  W: number,
  H: number,
) => {
  ctx.clearRect(0, 0, W, H);

  // Soft cream-paper background — actual color comes from the underlying
  // mesh material; here we lay subtle highlights so the baked-in text feels
  // hand-drawn rather than printed.
  ctx.fillStyle = "rgba(255,255,255,0)";
  ctx.fillRect(0, 0, W, H);

  // Title — bold marker headline. Rotated a hair off-axis with a tiny
  // positional wobble so it reads as scrawled, not typeset.
  {
    ctx.save();
    const j = handJitter(0);
    ctx.translate(W * 0.08 + j.dx, H * 0.22 + j.dy);
    ctx.rotate(j.rotate);
    ctx.fillStyle = WORKOUT_TITLE_COLOR;
    ctx.font = `${Math.round(H * 0.135)}px ${HANDWRITTEN_FONT_STACK}`;
    ctx.textBaseline = "alphabetic";
    ctx.fillText("AMRAP — 10 MIN", 0, 0);
    ctx.restore();
  }

  // Underline for the title — hand-drawn marker stroke with a slight
  // wave so it doesn't look mechanical.
  ctx.strokeStyle = WORKOUT_ACCENT_COLOR;
  ctx.lineWidth = H * 0.012;
  ctx.lineCap = "round";
  ctx.beginPath();
  ctx.moveTo(W * 0.08, H * 0.27);
  ctx.bezierCurveTo(
    W * 0.3,
    H * 0.272,
    W * 0.55,
    H * 0.282,
    W * 0.78,
    H * 0.273,
  );
  ctx.stroke();

  // Body lines — bullet + reps + movement.
  ctx.font = `${Math.round(H * 0.105)}px ${HANDWRITTEN_FONT_STACK}`;

  const lines: Array<[string, string]> = [
    ["10", "PUSH-UPS"],
    ["15", "AIR SQUATS"],
    ["20", "SIT-UPS"],
    ["200m", "RUN"],
  ];
  const startY = H * 0.45;
  const lineGap = H * 0.13;
  lines.forEach(([reps, move], i) => {
    const j = handJitter(i + 1);
    const y = startY + i * lineGap;

    // Bullet dash — subtly tilted and offset per line.
    ctx.save();
    ctx.translate(W * 0.1 + j.dx * 0.4, y - H * 0.03 + j.dy * 0.4);
    ctx.rotate(j.rotate * 0.6);
    ctx.strokeStyle = WORKOUT_ACCENT_COLOR;
    ctx.lineWidth = H * 0.012;
    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.lineTo(W * 0.04, 0);
    ctx.stroke();
    ctx.restore();

    // Reps + movement painted in a single rotated frame so the whole
    // line shares one tilt — feels like one stroke of the wrist.
    ctx.save();
    ctx.translate(W * 0.16 + j.dx, y + j.dy);
    ctx.rotate(j.rotate);

    ctx.fillStyle = WORKOUT_ACCENT_COLOR;
    ctx.fillText(reps, 0, 0);

    ctx.fillStyle = WORKOUT_BODY_COLOR;
    const repsWidth = ctx.measureText(reps).width;
    ctx.fillText(move, repsWidth + W * 0.025, 0);
    ctx.restore();
  });
};

export const Whiteboard: React.FC<{
  readonly whiteboardLayout: WhiteboardLayout;
  readonly baseScale: number;
}> = ({ whiteboardLayout, baseScale }) => {
  const frame = useCurrentFrame();

  const screenShape = useMemo(
    () =>
      roundedRect({
        width: whiteboardLayout.width,
        height: whiteboardLayout.height,
        radius: whiteboardLayout.radius,
      }),
    [whiteboardLayout.height, whiteboardLayout.radius, whiteboardLayout.width],
  );

  // Static workout drawing — painted once into an offscreen canvas, then
  // mapped onto the board face. No frame-driven progression because the
  // user wants the workout already written when the whiteboard arrives.
  const [canvas] = useState(
    () => new OffscreenCanvas(TEXT_CANVAS_W, TEXT_CANVAS_H),
  );
  const [ctx] = useState(() => {
    const c = canvas.getContext("2d");
    if (!c) throw new Error("Failed to get 2D context for whiteboard canvas");
    return c;
  });
  const [texture] = useState(() => new CanvasTexture(canvas));
  const { invalidate } = useThree();

  // Wait for Permanent Marker to actually be available to canvas before
  // drawing. @remotion/google-fonts adds the FontFace to document.fonts
  // but OffscreenCanvas.fillText doesn't always pick it up on the very
  // first paint — explicit document.fonts.load() at the exact pixel
  // sizes we use guarantees the font is rasterized in time. We hold
  // Remotion via delayRender so the first emitted frame is already
  // painted with the marker font instead of the sans-serif fallback.
  const drawnRef = useRef(false);
  useEffect(() => {
    if (drawnRef.current) return;
    const handle = delayRender("Loading whiteboard handwriting font");
    const titlePx = Math.round(TEXT_CANVAS_H * 0.115);
    const bodyPx = Math.round(TEXT_CANVAS_H * 0.105);
    const probe = (px: number) =>
      typeof document !== "undefined"
        ? document.fonts.load(`${px}px "${permanentMarkerFamily}"`)
        : Promise.resolve([]);
    Promise.all([probe(titlePx), probe(bodyPx)])
      .catch((err) => {
        console.warn("[Whiteboard] font load probe failed", err);
      })
      .finally(() => {
        if (!drawnRef.current) {
          drawWorkout(ctx, TEXT_CANVAS_W, TEXT_CANVAS_H);
          texture.needsUpdate = true;
          drawnRef.current = true;
          invalidate();
        }
        continueRender(handle);
      });
  }, [ctx, texture, invalidate]);

  // Position: slide in from off-screen right → settled. The board stays
  // at the settled position for the rest of the composition (no
  // slide-out) — the user wants the workout text visible alongside the
  // running timer.
  const slideInProgress = interpolate(
    frame,
    [WHITEBOARD_SLIDE_IN_START_FRAME, WHITEBOARD_SLIDE_IN_END_FRAME],
    [0, 1],
    {
      easing: Easing.out(Easing.cubic),
      extrapolateLeft: "clamp",
      extrapolateRight: "clamp",
    },
  );

  const slideInStart = useMemo(
    () => getWhiteboardSlideInStartPosition(baseScale),
    [baseScale],
  );
  const settled = useMemo(
    () => getWhiteboardSettledPosition(baseScale),
    [baseScale],
  );
  const finalPos = lerpVec3(slideInStart, settled, slideInProgress);

  return (
    <group position={finalPos} rotation={WHITEBOARD_TILT}>
      {/* Frame chassis */}
      <RoundedBox
        width={whiteboardLayout.width}
        height={whiteboardLayout.height}
        radius={whiteboardLayout.radius}
        depth={whiteboardLayout.thickness}
        curveSegments={PHONE_CURVE_SEGMENTS}
        position={[-whiteboardLayout.width / 2, -whiteboardLayout.height / 2, 0]}
      >
        <meshPhongMaterial color={WHITEBOARD_FRAME_COLOR} shininess={20} />
      </RoundedBox>

      {/* Cream-paper face */}
      <mesh
        position={[
          -whiteboardLayout.width / 2,
          -whiteboardLayout.height / 2,
          whiteboardLayout.thickness + Z_FLICKER_PREVENTION,
        ]}
      >
        <shapeGeometry args={[screenShape]} />
        <meshBasicMaterial color={WHITEBOARD_SCREEN_COLOR} toneMapped={false} />
      </mesh>

      {/* Workout text overlay — separate plane so it sits cleanly on top of
          the cream face without z-fighting. */}
      <mesh
        position={[0, 0, whiteboardLayout.thickness + Z_FLICKER_PREVENTION * 2]}
      >
        <planeGeometry args={[whiteboardLayout.width, whiteboardLayout.height]} />
        <meshBasicMaterial map={texture} transparent toneMapped={false} />
      </mesh>
    </group>
  );
};
